import './bootstrap';

import { createApp, h } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createInertiaApp, Head, Link } from '@inertiajs/vue3';
import Honeybadger from '@honeybadger-io/js';
import HoneybadgerVue from '@honeybadger-io/vue';
import TextClamp from 'vue3-text-clamp';
import InlineSvg from 'vue-inline-svg';
import Vue3TouchEvents from 'vue3-touch-events';
import Datepicker from '@vuepic/vue-datepicker';
import VueTheMask from 'vue-the-mask';

import dayjs from 'dayjs';
import dayJsPluginRelativeTime from 'dayjs/plugin/relativeTime';
import dayJsPluginUtc from 'dayjs/plugin/utc';
import dayJsPluginTimezone from 'dayjs/plugin/timezone';
import dayJsPluginAdvancedFormat from 'dayjs/plugin/advancedFormat';
import dayJsPluginDuration from 'dayjs/plugin/duration';
import { i18nVue } from 'laravel-vue-i18n';
import tailwindColors from '../../storage/colors';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';

dayjs.extend(dayJsPluginRelativeTime);
dayjs.extend(dayJsPluginUtc);
dayjs.extend(dayJsPluginTimezone);
dayjs.extend(dayJsPluginAdvancedFormat);
dayjs.extend(dayJsPluginDuration);

const appName = import.meta.env.VITE_APP_NAME || 'Classward';

window.colors = tailwindColors;

const honeyBadgerConfig = {
    apiKey: import.meta.env.VITE_HONEYBADGER_API_KEY,
    environment: process.env.NODE_ENV,
    revision: '1',
};

// @honeybadger-io/js v6's client only collects beforeNotify handlers pushed
// through Client.prototype.beforeNotify(handler); a beforeNotify key passed
// to configure() is silently discarded and never runs. This filter must be
// registered here, on the imported singleton, or it does nothing.
//
// Guard one is noise suppression, not a behavioral fix: the screen-orientation
// AbortError originates entirely inside JW Player's fullscreen handling, no
// app code can reach that promise, and the rejection still happens in the
// browser — we simply stop reporting it.
Honeybadger.beforeNotify((notice) => {
    if (notice.name === 'AbortError' && String(notice.message).includes('screen.orientation')) {
        return false;
    }
    if (notice.name === 'AxiosError' && /status code (401|419)/.test(notice.message)) {
        return false;
    }
});

let app = createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) =>
        resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue', { eager: false })),
    progress: {
        delay: 0,
        color: '#DD0000',
        includeCSS: true,
        showSpinner: true,
    },
    setup({ el, App, props, plugin }) {
        let inertiaApp = createApp({ render: () => h(App, props) });

        inertiaApp.config.globalProperties.dayjs = dayjs;
        inertiaApp.config.globalProperties.colors = tailwindColors;

        inertiaApp
            .use(plugin)
            .use(ZiggyVue)
            .use(HoneybadgerVue, honeyBadgerConfig)
            .use(TextClamp)
            .use(Vue3TouchEvents)
            .use(VueTheMask)
            .use(i18nVue, {
                resolve: async (lang) => {
                    const langs = import.meta.glob('../../lang/*.json');
                    return await langs[`../../lang/php_${lang}.json`]();
                },
            })
            .mixin({
                methods: {
                    getImageUrl(name, ext) {
                        return new URL(`../images/${name}`, import.meta.url).href;
                    },
                },
            })

            // third party components
            .component('inline-svg', InlineSvg)
            .component('Head', Head)
            .component('Link', Link)
            .component('Datepicker', Datepicker)
            .component('Menu', Menu)
            .component('MenuButton', MenuButton)
            .component('MenuItems', MenuItems)
            .component('MenuItem', MenuItem);

        inertiaApp.mount(el);

        return inertiaApp;
    },
});
